// src/components/dashboards/DashboardChat.tsx
// Natural language query interface for the analytics dashboard.
// Shows results as a patient list with links, and allows filtering/export.

import React, { useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import {
  alpha,
  Box,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import HistoryIcon from "@mui/icons-material/History";
import { athenaColors } from "../../theme/palette";
import {
  glassContentSx,
  glassControl,
  glassInputSx,
  glassTableSx,
} from "../../theme/glassTokens";
import CloseIcon from "@mui/icons-material/Close";
import useNLQuery, { NLHistoryEntry, isAnalysisReport } from "hooks/useNLQuery";
import AnalysisReportView from "./AnalysisReportView";
import type { AnalysisReportData } from "./AnalysisReportView";
import DisambiguationPanel from "./DisambiguationPanel";
import NLResultCard from "./NLResultCard";
import { useTranslation } from "../../i18n";

// Curated progress-step labels for the streaming "thoughts" indicator. Maps a backend step CODE to
// an [i18n key, fallback] pair. The backend never sends user text — only codes — so all wording
// (and translation) lives here on the client.
const STEP_LABEL_KEYS: Record<string, [string, string]> = {
  understanding: ["dashboard_chat.step.understanding", "Understanding your question"],
  selecting: ["dashboard_chat.step.selecting", "Selecting the relevant data"],
  drafting: ["dashboard_chat.step.drafting", "Drafting queries"],
  checking: ["dashboard_chat.step.checking", "Cross-checking results"],
  ranking: ["dashboard_chat.step.ranking", "Ranking interpretations"],
  formatting: ["dashboard_chat.step.formatting", "Preparing your answer"],
};

interface DashboardChatProps {
  /** Callback to open export dialog with specific patient IDs */
  onExportSelected?: (ids: number[]) => void;
  /** Environment IDs from the dashboard filter (scopes NL queries) */
  environmentIds?: number[];
  /** Fires when the chat becomes active (has results/loading) or inactive */
  onActiveChange?: (active: boolean) => void;
  /** Called when the user clicks the dismiss (X) button to close results */
  onDismiss?: () => void;
  /** When true, the input bar is hidden (results-only view) */
  hideInputBar?: boolean;
  /** Fires when suggested follow-ups change (for parent to render) */
  onFollowupsChange?: (followups: string[]) => void;
  /** Rendered on the dark dashboard ground rather than on a pale card. */
  darkMode?: boolean;
}

export interface DashboardChatHandle {
  clearAll: () => void;
  submitFollowup: (question: string) => void;
  getSuggestedFollowups: () => string[];
}

const DashboardChat = React.forwardRef<DashboardChatHandle, DashboardChatProps>(
  ({ onExportSelected, environmentIds, onActiveChange, onDismiss, hideInputBar, onFollowupsChange, darkMode }, ref) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const {
    loading, result, conversation, error, history, historyLoading, progressStep,
    query, fetchHistory, clear, clearCache, newConversation,
  } = useNLQuery();

  const [input, setInput] = useState("");
  const [showHistory, setShowHistory] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const isActive = loading || !!result || !!error || conversation.length > 0;
  const prevActiveRef = useRef(isActive);
  useEffect(() => {
    if (prevActiveRef.current !== isActive) {
      prevActiveRef.current = isActive;
      onActiveChange?.(isActive);
    }
  }, [isActive, onActiveChange]);

  // Notify parent when follow-up suggestions change
  useEffect(() => {
    onFollowupsChange?.(result?.suggested_followups ?? []);
  }, [result?.suggested_followups, onFollowupsChange]);

  useImperativeHandle(ref, () => ({
    clearAll: () => {
      clear();
      newConversation();
      setInput("");
      setShowHistory(false);
    },
    submitFollowup: (question: string) => {
      if (loading) return;
      setInput("");
      query(question, environmentIds);
    },
    getSuggestedFollowups: () => result?.suggested_followups ?? [],
  }), [clear, newConversation, loading, query, environmentIds, result]);

  useEffect(() => {
    fetchHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = useCallback(
    (e?: React.FormEvent) => {
      e?.preventDefault();
      const q = input.trim();
      if (!q || loading) return;
      query(q, environmentIds);
      setInput("");
    },
    [input, loading, query, environmentIds]
  );

  const handleFollowup = useCallback(
    (suggestion: string) => {
      if (loading) return;
      setInput("");
      query(suggestion, environmentIds);
    },
    [loading, query, environmentIds]
  );

  // Disambiguation: the user picked one interpretation. Re-submit the ORIGINAL question with a
  // 'select' directive; the backend returns that reading's result (and remembers the choice).
  const handleSelectInterpretation = useCallback(
    (interpretationId: string) => {
      if (loading || !result) return;
      query(result.question, environmentIds, { op: "select", interpretation_id: interpretationId });
    },
    [loading, result, query, environmentIds]
  );

  // Disambiguation: the user typed a clarification instead of picking. Re-run guided.
  const handleRefineInterpretation = useCallback(
    (instruction: string) => {
      const instr = instruction.trim();
      if (loading || !result || !instr) return;
      query(result.question, environmentIds, { op: "refine", instruction: instr });
    },
    [loading, result, query, environmentIds]
  );

  const handleHistoryClick = (entry: NLHistoryEntry) => {
    setInput(entry.question);
    setShowHistory(false);
    newConversation();
    query(entry.question, environmentIds);
  };

  // Card style — Joan, 2026-05-27: in expanded (hideInputBar) mode the
  // NL result card carries the SAME chrome as the V2 patient creation form
  // (secondary.main @ 0.75 + blur(12px) + white-alpha border + glassShadow
  // + radius 3). Single floating surface, no nested glass.
  const cardSx = hideInputBar
    ? {
        borderRadius: 3,
        border: `1px solid ${alpha(
          theme.palette.common.white,
          darkMode ? 0.12 : 0.35,
        )}`,
        // secondary.main is periwinkle: at 75% this was a BRIGHT slab, correct
        // on the old pastel dashboard and a different application on the dark
        // one. The result view is where the dashboard used to change identity
        // mid-task.
        background: darkMode
          ? alpha(athenaColors.deepNavy, 0.55)
          : alpha(
              theme.palette.secondary.main,
              theme.custom?.containerOpacity?.medium || 0.75,
            ),
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        // Joan, 2026-05-27: tighter two-layer shadow — same recipe as V2.
        boxShadow: `0 1px 2px ${alpha(
          theme.palette.primary.dark,
          0.08,
        )}, 0 6px 18px ${alpha(theme.palette.primary.dark, 0.12)}`,
        overflow: "hidden" as const,
        // Folded into cardSx rather than applied at one call site, because
        // every consumer of this surface (the result card, the analysis
        // report, the disambiguation panel, the history dropdown) has the same
        // problem: its contents were written for a white card. These are
        // descendant rules, so they reach children without editing them.
        ...(darkMode ? { ...glassContentSx, ...glassTableSx } : {}),
      }
    : {
        borderRadius: 2,
        border: `1px solid ${
          darkMode
            ? alpha(theme.palette.common.white, 0.12)
            : alpha(theme.palette.divider, 0.15)
        }`,
        background: darkMode
          ? alpha(athenaColors.deepNavy, 0.55)
          : alpha(theme.palette.background.paper, 0.5),
        backdropFilter: "blur(8px)",
        overflow: "hidden" as const,
        ...(darkMode ? { ...glassContentSx, ...glassTableSx } : {}),
      };

  return (
    <Box>
      {/* Input Bar + History — hidden when results are shown in expanded mode */}
      {!hideInputBar && (<>
      <Box component="form" onSubmit={handleSubmit}>
      <TextField
        inputRef={inputRef}
        value={input}
        onChange={(e) => setInput(e.target.value)}
        placeholder={t(
          "dashboard.nl.placeholder",
          "Ask a question about your patients..."
        )}
        fullWidth
        size="small"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <AutoAwesomeIcon
                sx={{
                  fontSize: darkMode ? 22 : 20,
                  color: darkMode ? athenaColors.periwinkle : "primary.main",
                  opacity: darkMode ? 1 : 0.7,
                  ...(darkMode && { mr: 0.5 }),
                }}
              />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end" sx={{ gap: 0.5, mr: -0.5 }}>
              {isActive && onDismiss && (
                <Tooltip title={t("dashboard_chat.dismiss", "Close results")}>
                  <IconButton
                    onClick={onDismiss}
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: "50%",
                      color: theme.palette.text.secondary,
                      "&:hover": {
                        bgcolor: alpha(theme.palette.action.hover, 0.3),
                      },
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title={t("dashboard_chat.query_history", "Query history")}>
                <IconButton
                  onClick={() => setShowHistory(!showHistory)}
                  sx={{
                    width: darkMode ? 38 : 32,
                    height: darkMode ? 38 : 32,
                    borderRadius: "50%",
                    bgcolor: darkMode
                      ? alpha(theme.palette.common.white, 0.08)
                      : alpha(theme.palette.info.main, 0.1),
                    color: darkMode
                      ? alpha(theme.palette.common.white, 0.75)
                      : theme.palette.info.main,
                    border: `1px solid ${
                      darkMode
                        ? alpha(athenaColors.periwinkle, 0.35)
                        : alpha(theme.palette.info.main, 0.25)
                    }`,
                    transition: "all .2s",
                    "&:hover": {
                      bgcolor: alpha(theme.palette.primary.main, 0.18),
                      color: theme.palette.primary.main,
                      borderColor: alpha(theme.palette.primary.main, 0.5),
                    },
                  }}
                >
                  <HistoryIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title={t("dashboard.nl.send", "Send")}>
                <span>
                  <IconButton
                    onClick={() => handleSubmit()}
                    disabled={!input.trim() || loading}
                    sx={{
                      width: darkMode ? 38 : 32,
                      height: darkMode ? 38 : 32,
                      borderRadius: "50%",
                      background: darkMode
                        ? `linear-gradient(180deg, ${athenaColors.royalBlue} 0%, ${alpha(
                            athenaColors.royalBlue,
                            0.85,
                          )} 100%)`
                        : `linear-gradient(180deg, ${alpha(
                            theme.palette.primary.main,
                            0.95,
                          )} 0%, ${alpha(theme.palette.primary.main, 0.85)} 100%)`,
                      color: theme.palette.common.white,
                      border: `1px solid ${alpha(
                        theme.palette.common.white,
                        0.25,
                      )}`,
                      transition: "all .25s ease",
                      "&:hover": {
                        background: `linear-gradient(180deg, ${alpha(
                          theme.palette.primary.dark,
                          1,
                        )} 0%, ${alpha(
                          theme.palette.primary.dark,
                          0.9,
                        )} 100%)`,
                        transform: "translateY(-1px)",
                      },
                      // text.primary is deepNavy: at 25% on a navy field the
                      // disabled arrow was an unreadable grey blob.
                      "&.Mui-disabled": {
                        background: alpha(theme.palette.common.white, 0.08),
                        color: darkMode
                          ? alpha(theme.palette.common.white, 0.35)
                          : alpha(theme.palette.text.primary, 0.25),
                        border: `1px solid ${alpha(
                          darkMode
                            ? theme.palette.common.white
                            : theme.palette.divider,
                          0.2,
                        )}`,
                      },
                    }}
                  >
                    {loading ? (
                      <CircularProgress size={16} sx={{ color: "inherit" }} />
                    ) : (
                      <SendIcon sx={{ fontSize: 16 }} />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            </InputAdornment>
          ),
        }}
        sx={{
          ...(darkMode ? glassInputSx : {}),
          "& .MuiOutlinedInput-root": {
            ...(darkMode ? glassInputSx["& .MuiOutlinedInput-root"] : {}),
            borderRadius: darkMode ? glassControl.radius : 2,
            // The sparkle needs its horizontal padding: the default 14px sat
            // it hard against the field's left edge.
            ...(darkMode
              ? { height: glassControl.height, pl: 2, pr: 1 }
              : {
                  backgroundColor: alpha(theme.palette.background.paper, 0.5),
                  backdropFilter: "blur(8px)",
                }),
            fontSize: "0.875rem",
          },
        }}
      />
      </Box>

      {/* History dropdown */}
      <Collapse in={showHistory}>
        <Box sx={{ ...cardSx, mt: 1, p: 1.5 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1,
            }}
          >
            <Typography
              variant="caption"
              fontWeight={600}
              color="text.secondary"
            >
              {t("dashboard_chat.recent_queries", "Recent Queries")}
            </Typography>
            <IconButton
              size="small"
              onClick={() => setShowHistory(false)}
            >
              <CloseIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Box>
          {historyLoading && <CircularProgress size={16} />}
          {!historyLoading && history.length === 0 && (
            <Typography variant="caption" color="text.secondary">
              No queries yet.
            </Typography>
          )}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            {history.slice(0, 10).map((entry) => (
              <Box
                key={entry.id}
                onClick={() => handleHistoryClick(entry)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  p: 0.75,
                  borderRadius: 1.5,
                  cursor: "pointer",
                  "&:hover": {
                    background: alpha(theme.palette.action.hover, 0.08),
                  },
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    flex: 1,
                    fontSize: "0.8rem",
                    color:
                      theme.custom?.contentText?.primary ||
                      theme.palette.text.primary,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {entry.question}
                </Typography>
                <Chip
                  label={`${entry.result_count} rows`}
                  size="small"
                  variant="outlined"
                  sx={{ height: 20, fontSize: "0.65rem" }}
                />
              </Box>
            ))}
          </Box>
        </Box>
      </Collapse>
      </>)}

      {/* Loading state */}
      {loading && (
        <Box
          sx={{
            ...cardSx,
            mt: 1.5,
            p: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
          }}
        >
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">
            {progressStep && STEP_LABEL_KEYS[progressStep]
              ? t(STEP_LABEL_KEYS[progressStep][0], STEP_LABEL_KEYS[progressStep][1])
              : t("dashboard.nl.thinking", "Analyzing your question...")}
          </Typography>
        </Box>
      )}

      {/* Error */}
      {error && !loading && (
        <Box
          sx={{
            ...cardSx,
            mt: 1.5,
            p: 2,
            borderColor: alpha(theme.palette.error.main, 0.3),
          }}
        >
          <Typography variant="body2" color="error">
            {error}
          </Typography>
        </Box>
      )}

      {/* Prior conversation turns (collapsed) */}
      {conversation.length > 1 && !loading && (
        <Box sx={{ mt: 1.5 }}>
          {conversation.slice(0, -1).map((turn, idx) => (
            <Box
              key={`turn-${idx}`}
              sx={{
                ...cardSx,
                mb: 0.75,
                p: 1.5,
                opacity: 0.7,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <AutoAwesomeIcon sx={{ fontSize: 14, color: theme.palette.primary.main, opacity: 0.6 }} />
                <Typography variant="body2" sx={{ fontSize: "0.8rem", fontWeight: 500, flex: 1 }}>
                  {turn.question}
                </Typography>
                {turn.intent && (
                  <Chip
                    label={turn.intent.intent}
                    size="small"
                    variant="outlined"
                    sx={{ height: 18, fontSize: "0.6rem" }}
                  />
                )}
                <Chip
                  label={`${turn.row_count} rows`}
                  size="small"
                  variant="outlined"
                  sx={{ height: 18, fontSize: "0.6rem" }}
                />
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block", fontSize: "0.72rem" }}>
                {turn.explanation}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {/* Mode B: Analysis Report */}
      {result && !loading && isAnalysisReport(result) && (
        <Box sx={{ mt: 1.5 }}>
          <AnalysisReportView report={result as unknown as AnalysisReportData} />

          {/* Follow-up chips — only in non-expanded mode */}
          {!hideInputBar && result.suggested_followups && result.suggested_followups.length > 0 && (
            <Box sx={{ display: "flex", gap: 0.5, mt: 1.5, flexWrap: "wrap" }}>
              {result.suggested_followups.map((s, si) => (
                <Chip
                  key={si}
                  label={s}
                  size="small"
                  variant="outlined"
                  clickable
                  onClick={() => handleFollowup(s)}
                  sx={{
                    fontSize: "0.72rem",
                    borderColor: alpha(theme.palette.primary.main, 0.3),
                    "&:hover": {
                      borderColor: theme.palette.primary.main,
                      background: alpha(theme.palette.primary.main, 0.08),
                    },
                  }}
                />
              ))}
            </Box>
          )}
        </Box>
      )}

      {/* Disambiguation (feature 3): the question reads several ways — let the user choose. */}
      {result && !loading && !isAnalysisReport(result) &&
        result.decision === "ambiguous" && result.options && result.options.length > 0 && (
        <DisambiguationPanel
          result={result}
          onSelect={handleSelectInterpretation}
          onRefine={handleRefineInterpretation}
          onExportSelected={onExportSelected}
          hideInputBar={hideInputBar}
        />
      )}

      {/* Current result (Mode A — single query). Skipped when an ambiguous panel is showing. */}
      {result && !loading && !isAnalysisReport(result) &&
        !(result.decision === "ambiguous" && result.options && result.options.length > 0) && (
        <Box sx={{ ...cardSx, mt: 1.5 }}>
          <NLResultCard
            result={result}
            environmentIds={environmentIds}
            hideInputBar={hideInputBar}
            onExportSelected={onExportSelected}
            onFollowup={handleFollowup}
            onClearCache={clearCache}
          />
        </Box>
      )}

    </Box>
  );
});

DashboardChat.displayName = "DashboardChat";

export default DashboardChat;
